// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Shelter Management Types
// ═══════════════════════════════════════════════════════════════

// ── Status Unions ────────────────────────────────────────────

export type ShelterStatus = 'open' | 'closed' | 'at_capacity' | 'standby';
export type SurvivorStatus = 'pre-registered' | 'checked_in' | 'transferred' | 'discharged' | 'missing';
export type PetStatus = 'sheltered' | 'transferred' | 'discharged' | 'missing';
export type VehicleStatus = 'parked' | 'released' | 'impounded';
export type TransferStatus = 'pending' | 'in_transit' | 'completed' | 'cancelled';
export type SupplyCategory = 'bedding' | 'food' | 'water' | 'hygiene' | 'medical' | 'equipment' | 'other';
export type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical';
export type ReferralStatus = 'pending' | 'in_progress' | 'completed' | 'declined';
export type ReunificationStatus = 'searching' | 'matched' | 'reunified' | 'closed';
export type MealPeriod = 'breakfast' | 'lunch' | 'dinner' | 'snack';

// ── Shelter ──────────────────────────────────────────────────

export interface Shelter {
  id: number;
  Rid: number;
  incidentId: string;
  name: string;
  address: string;
  latitude: string;
  longitude: string;
  capacity: number;
  currentPopulation: number;
  isAda: boolean;
  petFriendly: boolean;
  status: ShelterStatus;
  shelterType: string;       // school, community center, church, arena, etc.
  facilities: string;        // JSON array: ["showers","kitchen","laundry","wifi","generator","medical"]
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  operatingOrg: string;      // Red Cross, FEMA, local gov, etc.
  openedAt: string;
  closedAt: string;
  notes: string;
}

// ── Survivor ─────────────────────────────────────────────────

export interface Survivor {
  id: number;
  Rid: number;
  incidentId: string;
  shelterId: string;
  familyId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  phone: string;
  email: string;
  homeAddress: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  photoUrl: string;
  qrCode: string;
  status: SurvivorStatus;
  isMinor: boolean;
  isUnaccompaniedMinor: boolean;
  guardianId: string;        // for unaccompanied minors
  cotAssignment: string;     // e.g., "Wing B, Cot 14"
  specialNeeds: string;      // JSON array: ["wheelchair","oxygen","interpreter_spanish","hearing_impaired"]
  dietaryNeeds: string;      // JSON array: ["diabetic","halal","vegetarian","allergy_peanut"]
  medications: string;       // JSON: [{name, dosage, frequency, lastAdministered}]
  emergencyContactName: string;
  emergencyContactPhone: string;
  intakeTimestamp: string;
  dischargeTimestamp: string;
  dischargeReason: string;
  dischargedBy: string;
  intakeBy: string;
  notes: string;
}

// ── Family ───────────────────────────────────────────────────

export interface ShelterFamily {
  id: number;
  Rid: number;
  incidentId: string;
  familyName: string;
  headOfHouseholdId: string;
  memberIds: string;         // JSON array of survivor IDs
  memberCount: number;
  notes: string;
}

// ── Pet ──────────────────────────────────────────────────────

export interface ShelterPet {
  id: number;
  Rid: number;
  incidentId: string;
  shelterId: string;
  ownerId: string;
  name: string;
  species: string;           // dog, cat, bird, reptile, other
  breed: string;
  color: string;
  weight: string;
  qrCode: string;
  status: PetStatus;
  medicalNeeds: string;
  locationInShelter: string;
  photoUrl: string;
  notes: string;
}

// ── Vehicle ──────────────────────────────────────────────────

export interface ShelterVehicle {
  id: number;
  Rid: number;
  incidentId: string;
  shelterId: string;
  ownerId: string;
  make: string;
  model: string;
  year: string;
  color: string;
  licensePlate: string;
  state: string;
  vin: string;
  qrCode: string;
  parkingLocation: string;
  status: VehicleStatus;
  photoUrl: string;
  notes: string;
}

// ── Triage ───────────────────────────────────────────────────

export interface TriageQuestion {
  id: number;
  Rid: number;
  incidentId: string;
  question: string;
  questionType: string;      // 'yes_no' | 'multiple_choice' | 'text' | 'scale'
  options: string;           // JSON array for multiple choice
  category: string;          // 'medical' | 'housing' | 'safety' | 'general'
  sortOrder: number;
  isActive: boolean;
  flagIfAnswer: string;      // answer value that triggers a flag/alert
}

export interface TriageAnswer {
  id: number;
  Rid: number;
  survivorId: string;
  questionId: string;
  answer: string;
  isFlagged: boolean;
  answeredAt: string;
  answeredBy: string;
}

// ── Medical Log ──────────────────────────────────────────────

export interface MedicalLogEntry {
  id: number;
  Rid: number;
  incidentId: string;
  survivorId: string;
  shelterId: string;
  entryType: string;         // 'vitals' | 'medication' | 'incident' | 'note' | 'referral'
  description: string;
  medicationName: string;
  dosage: string;
  administeredBy: string;
  timestamp: string;
  followUpNeeded: boolean;
  followUpDate: string;
  notes: string;
}

// ── Transfer ─────────────────────────────────────────────────

export interface ShelterTransfer {
  id: number;
  Rid: number;
  incidentId: string;
  survivorId: string;
  survivorName: string;
  fromShelterId: string;
  fromShelterName: string;
  toShelterId: string;
  toShelterName: string;
  reason: string;
  status: TransferStatus;
  initiatedBy: string;
  initiatedAt: string;
  completedAt: string;
  notes: string;
}

// ── Property ─────────────────────────────────────────────────

export interface ShelterProperty {
  id: number;
  Rid: number;
  incidentId: string;
  shelterId: string;
  ownerId: string;
  description: string;
  category: string;          // 'bag' | 'documents' | 'electronics' | 'valuables' | 'other'
  storageLocation: string;
  qrCode: string;
  checkedInAt: string;
  checkedOutAt: string;
  checkedInBy: string;
  photoUrl: string;
  notes: string;
}

// ── Incident Report ──────────────────────────────────────────

export interface ShelterIncident {
  id: number;
  Rid: number;
  incidentId: string;
  shelterId: string;
  reportType: string;        // 'medical' | 'behavioral' | 'security' | 'maintenance' | 'other'
  severity: IncidentSeverity;
  description: string;
  involvedPartyIds: string;  // JSON array of survivor IDs
  respondingStaff: string;
  resolution: string;
  followUpNeeded: boolean;
  reportedAt: string;
  reportedBy: string;
  resolvedAt: string;
  notes: string;
}

// ── Referral ─────────────────────────────────────────────────

export interface ShelterReferral {
  id: number;
  Rid: number;
  incidentId: string;
  survivorId: string;
  survivorName: string;
  referralType: string;      // 'fema' | 'red_cross' | 'sba' | 'mental_health' | 'legal' | 'housing' | 'other'
  agency: string;
  caseNumber: string;
  status: ReferralStatus;
  dateMade: string;
  completedDate: string;
  madeBy: string;
  notes: string;
}

// ── Supply ───────────────────────────────────────────────────

export interface ShelterSupply {
  id: number;
  Rid: number;
  incidentId: string;
  shelterId: string;
  itemName: string;
  category: SupplyCategory;
  quantity: number;
  unit: string;              // 'each' | 'case' | 'pallet' | 'box'
  reorderThreshold: number;
  lastRestocked: string;
  notes: string;
}

// ── Reunification ────────────────────────────────────────────

export interface ReunificationRequest {
  id: number;
  Rid: number;
  incidentId: string;
  requestedByName: string;
  requestedByPhone: string;
  requestedByEmail: string;
  missingPersonName: string;
  missingPersonDescription: string;
  missingPersonDob: string;
  missingPersonPhone: string;
  missingPersonAge: string;
  source: string;
  lastKnownLocation: string;
  lastKnownDate: string;
  relationship: string;
  matchedSurvivorId: string;
  status: ReunificationStatus;
  photoUrl: string;
  createdAt: string;
  resolvedAt: string;
  notes: string;
}

// ── Constants ────────────────────────────────────────────────

export const SHELTER_STATUS_COLORS: Record<ShelterStatus, string> = {
  open: '#16a34a',
  closed: '#6b7280',
  at_capacity: '#d97706',
  standby: '#0284c7',
};

export const SURVIVOR_STATUS_COLORS: Record<SurvivorStatus, string> = {
  'pre-registered': '#7c3aed',
  checked_in: '#16a34a',
  transferred: '#0284c7',
  discharged: '#6b7280',
  missing: '#dc2626',
};

export const SHELTER_TYPES = [
  'school', 'community_center', 'church', 'arena', 'convention_center',
  'government_building', 'hotel', 'military', 'tent_city', 'other',
] as const;

export const SHELTER_TYPE_LABELS: Record<string, string> = {
  school: 'School', community_center: 'Community Center', church: 'Church/House of Worship',
  arena: 'Arena/Stadium', convention_center: 'Convention Center',
  government_building: 'Government Building', hotel: 'Hotel/Motel',
  military: 'Military Facility', tent_city: 'Tent City', other: 'Other',
};

export const FACILITY_OPTIONS = [
  { value: 'showers', label: 'Showers' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'wifi', label: 'Wi-Fi' },
  { value: 'generator', label: 'Generator/Backup Power' },
  { value: 'medical', label: 'Medical Station' },
  { value: 'childcare', label: 'Childcare Area' },
  { value: 'pet_area', label: 'Pet Area' },
  { value: 'parking', label: 'Parking Lot' },
  { value: 'outdoor_space', label: 'Outdoor Space' },
  { value: 'commercial_kitchen', label: 'Commercial Kitchen' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'security', label: 'Security' },
];

export const SPECIAL_NEEDS_OPTIONS = [
  { value: 'wheelchair', label: 'Wheelchair' },
  { value: 'oxygen', label: 'Oxygen' },
  { value: 'hearing_impaired', label: 'Hearing Impaired' },
  { value: 'vision_impaired', label: 'Vision Impaired' },
  { value: 'mobility_limited', label: 'Limited Mobility' },
  { value: 'interpreter_spanish', label: 'Interpreter - Spanish' },
  { value: 'interpreter_other', label: 'Interpreter - Other' },
  { value: 'service_animal', label: 'Service Animal' },
  { value: 'mental_health', label: 'Mental Health Support' },
  { value: 'infant', label: 'Infant/Nursing' },
  { value: 'elderly', label: 'Elderly (75+)' },
  { value: 'dialysis', label: 'Dialysis' },
];

export const DIETARY_OPTIONS = [
  { value: 'diabetic', label: 'Diabetic' },
  { value: 'halal', label: 'Halal' },
  { value: 'kosher', label: 'Kosher' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'gluten_free', label: 'Gluten Free' },
  { value: 'allergy_peanut', label: 'Peanut Allergy' },
  { value: 'allergy_shellfish', label: 'Shellfish Allergy' },
  { value: 'allergy_dairy', label: 'Dairy Allergy' },
  { value: 'infant_formula', label: 'Infant Formula' },
  { value: 'soft_food', label: 'Soft Food Only' },
  { value: 'other', label: 'Other' },
];

export const REFERRAL_TYPES = [
  { value: 'fema', label: 'FEMA Registration' },
  { value: 'red_cross', label: 'Red Cross Casework' },
  { value: 'sba', label: 'SBA Loan' },
  { value: 'mental_health', label: 'Mental Health' },
  { value: 'legal', label: 'Legal Aid' },
  { value: 'housing', label: 'Housing Assistance' },
  { value: 'medical', label: 'Medical Referral' },
  { value: 'financial', label: 'Financial Assistance' },
  { value: 'other', label: 'Other' },
];

export const INCIDENT_REPORT_TYPES = [
  { value: 'medical', label: 'Medical Emergency' },
  { value: 'behavioral', label: 'Behavioral Issue' },
  { value: 'security', label: 'Security Concern' },
  { value: 'maintenance', label: 'Maintenance/Facility' },
  { value: 'theft', label: 'Theft Report' },
  { value: 'other', label: 'Other' },
];

export const SUPPLY_CATEGORIES: { value: SupplyCategory; label: string }[] = [
  { value: 'bedding', label: 'Bedding (Cots, Blankets, Pillows)' },
  { value: 'food', label: 'Food' },
  { value: 'water', label: 'Water' },
  { value: 'hygiene', label: 'Hygiene Kits' },
  { value: 'medical', label: 'Medical Supplies' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'other', label: 'Other' },
];

export const PET_SPECIES = [
  { value: 'dog', label: 'Dog' },
  { value: 'cat', label: 'Cat' },
  { value: 'bird', label: 'Bird' },
  { value: 'reptile', label: 'Reptile' },
  { value: 'small_animal', label: 'Small Animal (Hamster, Rabbit, etc.)' },
  { value: 'other', label: 'Other' },
];
